/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from '@msdyn365-commerce/core';
import { lAC_ITSResumeEcomCartAsync } from '../custom-actions/DataActionExtension.g';
import * as Entities from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import { ILAC_AuthToken } from '../custom-actions/DataServiceEntities.g';
import { refreshToken } from '../utils/authService';

/**
 * ResumeCart Input Action
 */
export class ResumeCartInput implements Msdyn365.IActionInput {
    public cartId: string;
    public authTokenCookie: ILAC_AuthToken;
    public customerId: string;

    constructor(cartId: string, authTokenCookie: ILAC_AuthToken, customerId: string) {
        this.cartId = cartId;
        this.authTokenCookie = authTokenCookie;
        this.customerId = customerId;
    }

    // a cache object type and an appropriate cache key
    public getCacheKey = () => `ITSRESUMEECOMCART`;
    public getCacheObjectType = () => 'ITSRESUMEECOMCART';
    public dataCacheType = (): Msdyn365.CacheType => 'none';
}

async function resumeCartAsync(input: ResumeCartInput, ctx: Msdyn365.IActionContext): Promise<Entities.Cart | undefined> {
    const authToken = input.authTokenCookie;

    if (authToken && authToken.IntuitDomeAccountId && authToken.Token && authToken.RefreshToken) {
        try {
            const result = await lAC_ITSResumeEcomCartAsync({ callerContext: ctx }, authToken, input.cartId, input.customerId);
            if (result) {
                return result;
            }
        } catch (error) {
            console.log('Error resuming cart', error);

            const actionError = error as {
                data?: {
                    ErrorResourceId?: string;
                };
            };

            const unAuthorized = actionError.data?.ErrorResourceId === '401';
            if (unAuthorized) {
                const refreshedToken = await refreshToken(ctx);
                if (refreshedToken) {
                    try {
                        const result = await lAC_ITSResumeEcomCartAsync(
                            { callerContext: ctx },
                            refreshedToken,
                            input.cartId,
                            input.customerId
                        );
                        if (result) {
                            return result;
                        }
                    } catch (error) {
                        console.log('Error resuming cart', error);
                    }
                }
            }
        }
    }

    return undefined;
}

export default Msdyn365.createObservableDataAction({
    action: <Msdyn365.IAction<Entities.Cart | undefined>>resumeCartAsync,
    id: 'ITSResumeEcomCart'
});
